import { paths } from '@/constants/paths';
import {
  DEFAULT_CLIENT_DETAIL_TAB,
  isClientDetailTabKey,
  type ClientDetailTabKey,
} from '@/modules/clientServices/utils/clientDetailTabs';

export type AdvisorClientDetailQueryState =
  | { mode: 'list' }
  | { mode: 'detail'; investorId: number }
  | { mode: 'invalid' };

function parseInvestorId(value: string): number | undefined {
  if (!/^[1-9]\d*$/.test(value)) return undefined;

  const investorId = Number(value);
  return Number.isSafeInteger(investorId) ? investorId : undefined;
}

export function parseAdvisorClientDetailSearchParams(
  searchParams: URLSearchParams,
): AdvisorClientDetailQueryState {
  const investorIds = searchParams.getAll('investorId');

  if (investorIds.length === 0) return { mode: 'list' };
  if (investorIds.length !== 1) return { mode: 'invalid' };

  const investorId = parseInvestorId(investorIds[0]);
  return investorId === undefined
    ? { mode: 'invalid' }
    : { mode: 'detail', investorId };
}

export function buildAdvisorClientDetailUrl(
  investorId: number,
  tabKey?: ClientDetailTabKey,
): string {
  const searchParams = new URLSearchParams({
    investorId: String(investorId),
  });
  if (tabKey) searchParams.set('tab', tabKey);

  return `${paths.advisor.clients.index}?${searchParams.toString()}`;
}

export function canonicalizeAdvisorClientDetailTabSearchParams(
  searchParams: URLSearchParams,
): URLSearchParams {
  const canonical = new URLSearchParams(searchParams);
  const tabs = canonical.getAll('tab');

  if (tabs.length === 0) return canonical;
  if (tabs.length !== 1 || !isClientDetailTabKey(tabs[0])) {
    canonical.set('tab', DEFAULT_CLIENT_DETAIL_TAB);
  }

  return canonical;
}

export function updateAdvisorClientDetailTabSearchParams(
  searchParams: URLSearchParams,
  tabKey: ClientDetailTabKey,
): URLSearchParams {
  const updated = new URLSearchParams(searchParams);
  updated.set('tab', tabKey);
  return updated;
}
